import { getToken, CompanyId, Language, UserTypeId, User, ListOptions, NonMasterUserTypeId } from "helpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { environment } from "environment";
import { UsersResponse } from "./apiHelpers";

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${environment.dev.baseUrl}/auth/user/manage`,
    prepareHeaders: (headers) => {
      headers.set("authorization-token", getToken() || "");
      return headers;
    },
  }),

  endpoints: (builder) => ({
    createUser: builder.mutation<User, CreateUserArgs>({
      query: ({
        company_id,
        first_name,
        last_name,
        email,
        phone_number,
        parking,
        targetGroup,
        language,
        type,
        notification,
        address,
        municipality,
      }) => ({
        url: `${company_id}`,
        method: "PUT",
        body: {
          first_name,
          last_name,
          email,
          phone_number,
          parking,
          targetGroup,
          language,
          type,
          notification,
          address,
          municipality,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    getUser: builder.query<User, GetUserArgs>({
      query: ({ company_id, uuid }) => ({
        url: `${company_id}/${uuid}`,
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    updateUser: builder.mutation<User, UpdateUserArgs>({
      query: ({
        company_id,
        uuid,
        first_name,
        last_name,
        email,
        phone_number,
        address,
        municipality,
        language,
        notification,
        type,
      }) => ({
        url: `${company_id}/${uuid}`,
        method: "POST",
        body: {
          first_name,
          last_name,
          email,
          phone_number,
          language,
          address,
          notification,
          municipality,
          type,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    deleteUser: builder.mutation<null, UserDelete>({
      query: ({ company_id, uuid, otcode }) => ({
        url: `${company_id}/${uuid}`,
        method: "DELETE",
        params: {
          otcode: otcode,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    getUsers: builder.query<UsersResponse, ListOptions>({
      query: ({ page = 0, pageSize = 5, company_id, search, status, type, sort, order }) => ({
        url: `list/${company_id}`,
        params: {
          page: page + 1,
          pageSize,
          search: search ? search.replace(/ /g, "-") : undefined,
          status,
          type,
          sort,
          order,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    changeStatus: builder.mutation<User, ChangeStatusArgs>({
      query: ({ company_id, uuid, userStatus, otcode }) => ({
        url: `change-status/${company_id}/${uuid}`,
        method: "POST",
        params: {
          otcode: otcode,
        },
        body: {
          userStatus,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    resendTemporaryPassword: builder.mutation<null, ResendTemporaryPasswordArgs>({
      query: ({ company_id, uuid, temporaryPassword }) => ({
        url: `resend-temporary-password/${company_id}/${uuid}`,
        method: "POST",
        body: {
          temporaryPassword,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    checkEmail: builder.query<any, CheckEmailArgs>({
      query: ({ company_id, email }) => ({
        url: `${company_id}/${email}`,
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),
  }),
});

// Export hooks for usage in functional components, which are auto-generated based on the defined endpoints
export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useChangeStatusMutation,
  useDeleteUserMutation,
  useResendTemporaryPasswordMutation,
  useCheckEmailQuery,
  useLazyCheckEmailQuery,
  usePrefetch: useUserApiPrefetch,
} = userApi;

type UserSubset = Pick<User, "company_id" | "uuid">;
type UserDelete = Pick<User, "company_id" | "uuid"> & { otcode: number };
type UserStatus = "enable" | "disable";

interface CreateUserArgs
  extends Pick<User, "company_id" | "first_name" | "last_name" | "email" | "phone_number" | "address" | "language">,
    Partial<Pick<User, "municipality">> {
  parking: string;
  targetGroup: CompanyId;
  type: NonMasterUserTypeId;
  notification: boolean;
}

interface GetUserArgs extends UserSubset {}

interface CheckEmailArgs extends Pick<User, "company_id" | "email"> {}

export interface UpdateUserArgs
  extends UserSubset,
    Partial<Pick<User, "first_name" | "last_name" | "email" | "phone_number">> {
  notification?: string;
  address?: string;
  language?: Language;
  municipality?: string;
  type?: UserTypeId;
}

interface ChangeStatusArgs extends UserSubset {
  userStatus: UserStatus;
  otcode: number;
}

interface ResendTemporaryPasswordArgs extends UserSubset {
  temporaryPassword?: string;
}
